import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { BsFillExclamationTriangleFill } from 'react-icons/bs';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

function DueForCheckIn() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState(null);
  const [phone, setPhone] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [processingBookings, setProcessingBookings] = useState({}); // Track processing per booking
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [hotelEmail, setHotelEmail] = useState('support@example.com');
  const [hotelPhone, setHotelPhone] = useState('+1-234-567-8900');
  const [earlyCheckinData, setEarlyCheckinData] = useState(null); // Store early check-in data
  const navigate = useNavigate();

  const getGuestIdFromToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub;
    } catch (e) {
      console.error('Error parsing token:', e);
      return null;
    }
  };

  // Load hotel data from local storage
  useEffect(() => {
    const cachedHotelData = localStorage.getItem('hotelPropertyCache');
    if (cachedHotelData) {
      try {
        const parsedData = JSON.parse(cachedHotelData);
        if (parsedData.data) {
          setHotelEmail(parsedData.data.email || 'support@example.com');
          setHotelPhone(parsedData.data.phone || '+1-234-567-8900');
        }
      } catch (error) {
        console.error('Error parsing hotel data from cache:', error);
      }
    }
  }, []);

  const decryptUserData = async (encryptedData) => {
    try {
      if (encryptedData.email && encryptedData.email.length < 50) {
        return encryptedData;
      }

      const accessToken = Cookies.get('access_token');
      if (!accessToken) {
        throw new Error('No access token found');
      }

      const response = await fetch(`${BQ_BASE_URL}/bq/api/decrypt-fields`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          encrypted_fields: {
            email: encryptedData.email,
            phone_number: encryptedData.phone_number,
            country_code: encryptedData.country_code,
          },
        }),
      });

      if (response.status === 401) {
        return encryptedData;
      }

      if (!response.ok) {
        throw new Error(`Decryption failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Decryption error:', error);
      return encryptedData;
    }
  };

  const getGuestData = async () => {
    const accessToken = Cookies.get('access_token');
    if (!accessToken) return null;

    try {
      const guestId = Cookies.get('guest_id') || getGuestIdFromToken(accessToken);
      if (!guestId) return null;

      const profileResponse = await fetch(`${BQ_BASE_URL}/bq/api/profile?guest_id=${guestId}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!profileResponse.ok) {
        throw new Error(`Profile API Error: ${profileResponse.statusText}`);
      }

      const profileData = await profileResponse.json();
      const isDecrypted = profileData.emailid && profileData.emailid.length < 50;

      if (isDecrypted) {
        // Store phone number in state and cookie
        if (profileData.phonenumber) {
          Cookies.set('phone_number', profileData.phonenumber, { expires: 1 });
          setPhone(profileData.phonenumber);
        }

        return {
          firstname: profileData.firstname || '',
          lastname: profileData.lastname || '',
          email: profileData.emailid || '',
          phone: profileData.phonenumber || '',
        };
      }

      const decryptedFields = await decryptUserData({
        email: profileData.emailid,
        phone_number: profileData.phonenumber,
        country_code: profileData.countrycode,
      });

      // Store phone number in cookie and state after decryption
      if (decryptedFields.phone_number) {
        Cookies.set('phone_number', decryptedFields.phone_number, { expires: 1 });
        setPhone(decryptedFields.phone_number);
      }

      return {
        firstname: profileData.firstname || '',
        lastname: profileData.lastname || '',
        email: decryptedFields.email || profileData.emailid || '',
        phone: decryptedFields.phone_number || profileData.phonenumber || '',
      };
    } catch (error) {
      console.error('Error fetching guest data:', error);
      return null;
    }
  };

  useEffect(() => {
    // Fetch guest data to get email and phone
    const fetchGuestContactInfo = async () => {
      try {
        const guestData = await getGuestData();

        // Also check if phone number is available in cookies
        const phoneFromCookie = Cookies.get('phone_number');

        if (phoneFromCookie) {
          setPhone(phoneFromCookie);
        }

        if (guestData) {
          if (guestData.email) {
            setEmail(guestData.email);
          }
          if (guestData.phone) {
            setPhone(guestData.phone);
            Cookies.set('phone_number', guestData.phone, { expires: 1 });
          }
        }

        // If we don't have any contact info, show error
        if (!guestData?.email && !phoneFromCookie && !guestData?.phone) {
          throw new Error('No contact information found for guest');
        }
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchGuestContactInfo();
  }, []);

  useEffect(() => {
    // Don't fetch if we don't have any contact info
    if (!email && !phone) return;

    // Fetch eligible bookings using either email or phone
    const fetchBookings = async () => {
      try {
        let url;
        let searchMethod = '';

        // Prioritize email if available, otherwise use phone
        if (email) {
          url = `${BQ_BASE_URL}/bq/api/checkin-preview/by-email?email=${encodeURIComponent(email)}`;
          searchMethod = 'email';
        } else if (phone) {
          url = `${BQ_BASE_URL}/bq/api/checkin-preview/by-email?phone=${encodeURIComponent(phone)}`;
          searchMethod = 'phone';
        }

        console.log(`Fetching bookings using ${searchMethod}:`, url);

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            accept: 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch bookings');
        }

        const data = await response.json();

        if (data.success && data.bookings && data.bookings.length > 0) {
          setBookings(data.bookings);
          setError(null);
        } else {
          // If no bookings found with primary method, try the other method if available
          if (searchMethod === 'email' && phone) {
            console.log('No bookings found with email, trying phone...');
            const phoneUrl = `${BQ_BASE_URL}/bq/api/checkin-preview/by-email?phone=${encodeURIComponent(phone)}`;
            const phoneResponse = await fetch(phoneUrl, {
              method: 'GET',
              headers: {
                accept: 'application/json',
              },
            });

            if (phoneResponse.ok) {
              const phoneData = await phoneResponse.json();
              if (phoneData.success && phoneData.bookings && phoneData.bookings.length > 0) {
                setBookings(phoneData.bookings);
                setError(null);
                return;
              }
            }
          } else if (searchMethod === 'phone' && email) {
            console.log('No bookings found with phone, trying email...');
            const emailUrl = `${BQ_BASE_URL}/bq/api/checkin-preview/by-email?email=${encodeURIComponent(email)}`;
            const emailResponse = await fetch(emailUrl, {
              method: 'GET',
              headers: {
                accept: 'application/json',
              },
            });

            if (emailResponse.ok) {
              const emailData = await emailResponse.json();
              if (emailData.success && emailData.bookings && emailData.bookings.length > 0) {
                setBookings(emailData.bookings);
                setError(null);
                return;
              }
            }
          }

          setError(data.message || 'No bookings found');
        }
      } catch (err) {
        console.error('Error fetching bookings:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [email, phone]);

  // Function to check if early check-in disclaimer should be shown
  const shouldShowDisclaimer = (checkInDate) => {
    const now = new Date();
    const checkInDateTime = new Date(checkInDate);

    // Check if the check-in date is today
    const isToday = checkInDateTime.toDateString() === now.toDateString();

    // If it's not today, don't show disclaimer
    if (!isToday) {
      return false;
    }

    // Set check-in time to 1:00 PM
    checkInDateTime.setHours(12, 0, 0, 0);

    // Calculate 2 hours prior to check-in time (11:00 AM)
    const twoHoursPrior = new Date(checkInDateTime);
    twoHoursPrior.setHours(twoHoursPrior.getHours() - 2);

    // If current time is before the 2 hours prior to check-in, show disclaimer
    return now < twoHoursPrior;
  };

  // Function to check if check-in is allowed (today or tomorrow)
  const isCheckInAllowed = (checkInDate) => {
    const now = new Date();
    const checkInDateTime = new Date(checkInDate);

    // Allow check-in for today and tomorrow
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const checkInDay = new Date(checkInDateTime);
    checkInDay.setHours(0, 0, 0, 0);

    return checkInDay.getTime() === today.getTime() || checkInDay.getTime() === tomorrow.getTime();
  };

  const handleCheckInClick = async (booking) => {
    // First check if check-in is allowed for this booking
    if (!isCheckInAllowed(booking.checkindate)) {
      setPopupMessage("Check-in is only available for today's and tomorrow's bookings");
      setShowPopup(true);
      return;
    }

    // If early check-in, fetch early check-in details
    if (shouldShowDisclaimer(booking.checkindate)) {
      try {
        setBookingProcessing(booking.bookingid, true);
        const response = await fetch(
          `${BQ_BASE_URL}/bq/api/pre-checkin/?order_id=${booking.orderid}&last_name=${booking.lastname}`,
          {
            method: 'GET',
            headers: {
              accept: 'application/json',
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.bookings && data.bookings.length > 0) {
            setEarlyCheckinData(data.bookings[0].early_checkin);
            setSelectedBooking(booking);
            setShowDisclaimer(true);
          } else {
            setPopupMessage(data.message || 'Failed to get check-in details');
            setShowPopup(true);
          }
        } else {
          setPopupMessage('Failed to get check-in details');
          setShowPopup(true);
        }
      } catch (err) {
        setPopupMessage('An error occurred while fetching check-in details');
        setShowPopup(true);
      } finally {
        setBookingProcessing(booking.bookingid, false);
      }
    } else {
      proceedWithCheckIn(booking);
    }
  };

  const setBookingProcessing = (bookingId, isProcessing) => {
    setProcessingBookings((prev) => ({
      ...prev,
      [bookingId]: isProcessing,
    }));
  };

  const proceedWithCheckIn = async (booking) => {
    setBookingProcessing(booking.bookingid, true);
    try {
      // Remove email validation and just send order_id and last_name
      const response = await fetch(`${BQ_BASE_URL}/bq/api/pre-checkin/`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_id: booking.orderid,
          last_name: booking.lastname,
          // Email parameter removed as it's not required by the API
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setPopupMessage(data.detail?.message || 'Check-in failed');
        setShowPopup(true);
        return;
      }

      if (data.success) {
        // Format the data to match the expected structure
        const bookingDetails = {
          success: data.success,
          message: data.message,
          today: data.today,
          order_id: data.order_id,
          primary_guest: data.primary_guest,
          bookings: data.bookings,
          multiple_rooms: data.multiple_rooms,
          attention_needed: data.attention_needed,
          primaryBooking: data.bookings[0], // Assuming first booking is primary
        };

        // Store formatted booking details in local storage
        localStorage.setItem('bookingDetails', JSON.stringify(bookingDetails));

        // Redirect to checkin-details page
        navigate('/checkin-details');
      } else {
        setPopupMessage(data.message || 'Check-in failed');
        setShowPopup(true);
      }
    } catch (err) {
      console.error('Check-in error:', err);
      setPopupMessage(err.message || 'An unexpected error occurred');
      setShowPopup(true);
    } finally {
      setBookingProcessing(booking.bookingid, false);
      setShowDisclaimer(false);
      setEarlyCheckinData(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  if (loading) {
    return <div className="text-center text-lg">Loading...</div>;
  }

  if (error) {
    return <div className="text-center text-3xl font-bold text-black">{error}</div>;
  }

  if (bookings.length === 0) {
    return <div className="text-center text-3xl font-bold mb-4">No upcoming check-ins found</div>;
  }

  return (
    <div className="mx-auto pb-24 mb-52 sm:pb-20">
      <div className="text-center">
        <h1 className="text-3xl font-bold mb-4">Upcoming Check-in</h1>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto shadow-md sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                >
                  Check-in
                </th>
                <th
                  scope="col"
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                >
                  Check-out
                </th>
                <th
                  scope="col"
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                >
                  Room Type
                </th>
                <th
                  scope="col"
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                >
                  Nights
                </th>
                <th
                  scope="col"
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {bookings.map((booking) => (
                <tr key={booking.bookingid} className="hover:bg-gray-50">
                  <td className="px-3 py-4">
                    <div className="text-sm sm:text-base">{formatDate(booking.checkindate)}</div>
                    <div className="text-xs text-gray-500">From 11:00 AM</div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="text-sm sm:text-base">{formatDate(booking.checkoutdate)}</div>
                    <div className="text-xs text-gray-500">Till 12:00 PM</div>
                  </td>
                  <td className="px-3 py-4 text-sm sm:text-base whitespace-nowrap">
                    {booking.room_type}
                  </td>
                  <td className="px-3 py-4 text-sm sm:text-base whitespace-nowrap">
                    {booking.lengthofstay}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleCheckInClick(booking)}
                      disabled={processingBookings[booking.bookingid]}
                      className="bg-black hover:bg-gray-800 text-white px-3 py-2 rounded-lg text-xs sm:text-sm w-full sm:w-auto disabled:opacity-50"
                    >
                      {processingBookings[booking.bookingid] ? 'Processing...' : 'Check In'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Disclaimer Modal */}
      {showDisclaimer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <h2 className="text-xl font-bold mb-4">Early Check-In Notice</h2>
            <p className="mb-4">
              You are attempting to check in before the standard check-in time of{' '}
              <strong>1:00 PM</strong>. Early check-in will be charged{' '}
              <strong>₹{earlyCheckinData?.extra_charge}</strong> and is subject to additional
              charges and room availability.
            </p>

            <p className="mb-6 font-semibold">Would you like to proceed with the early check-in?</p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setShowDisclaimer(false);
                  setEarlyCheckinData(null);
                }}
                disabled={processingBookings[selectedBooking?.bookingid]}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (selectedBooking) {
                    proceedWithCheckIn(selectedBooking);
                  }
                }}
                disabled={processingBookings[selectedBooking?.bookingid]}
                className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
              >
                {processingBookings[selectedBooking?.bookingid] ? 'Processing...' : 'Proceed'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Popup */}
      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="relative bg-white rounded-lg shadow-lg max-w-[550px] w-full p-6 mx-4">
            <button
              onClick={() => setShowPopup(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="text-center pt-2">
              <BsFillExclamationTriangleFill className="text-yellow-400 text-4xl mx-auto mb-3" />
              <h2 className="text-gray-900 text-lg font-semibold mb-2">Unable to Proceed</h2>
              <p className="text-red-600 text-md font-medium mb-3 whitespace-pre-line">
                {popupMessage}
              </p>
              <p className="text-gray-700 text-sm mb-4 mt-2">
                In case of any assistance please contact our reception assistance support team at{' '}
                <a href={`mailto:${hotelEmail}`} className="text-blue-600 hover:underline">
                  {hotelEmail}
                </a>{' '}
                or call us at{' '}
                <a href={`tel:${hotelPhone}`} className="text-blue-600 hover:underline">
                  {hotelPhone}
                </a>
              </p>

              <button
                onClick={() => setShowPopup(false)}
                className="px-5 mr-4 py-2 bg-black text-white rounded-md font-medium"
              >
                Try Again
              </button>
              <button
                onClick={() => {
                  setShowPopup(false);
                  navigate('/create-user-later');
                }}
                className=" px-5 py-2 bg-black text-white rounded-md font-medium"
              >
                Complete Profile
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DueForCheckIn;
